import React, { Component, useState, useEffect, memo } from "react";
import { useSelector, useDispatch } from 'react-redux';
import PQueue from 'p-queue';
import {getChannel} from 'postal';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import IconButton from '@mui/material/IconButton';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import DeleteIcon from '@mui/icons-material/Delete';
import Typography from '@mui/material/Typography';
import FormControl, { useFormControl } from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import Divider from '@mui/material/Divider';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import SubjectIcon from '@mui/icons-material/Subject';
import SvgIcon from '@mui/material/SvgIcon';


import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import { UserInfo, netGet, netPost, netFetch, keycloak } from "../network";
import { subscribe as messageSubscribe, unsubscribe as messageUnsubscribe, publish as messagePublish } from "../messenger";
import { InternalLinkChooserDialog } from "./link-chooser";
import { expand } from "rxjs";
const postal = getChannel('ucs');

const EFB_TYPES = [
    { id: 'DUCH', type: 'DU', mid: 'chapter', label: 'Chapter', 
        template:  `<?xml version="1.0" encoding="utf-8"?>
<chapter>
  <topic>
    <title></title>
    <para></para>
  </topic>
</chapter>
`},
];

const QRH_TYPES = [
    { id: 'PSLEICASACTION', type: 'PSL', mid: 'EICAS_ACTION_CHECKLIST', label: "EICAS Action Checklist", 
        template: `<?xml version="1.0" encoding="UTF-8"?>
<eicasActionChecklist>
  <title></title>

  <preamble>
    <condition>
      <para></para>
    </condition>
  </preamble>

  <referenceTaskGroup>
    <challengeResponse>
      <challenge></challenge>

      <response></response>
    </challengeResponse>
  </referenceTaskGroup>
</eicasActionChecklist>
`
     },
    { id: 'PSLEICASINFO', type: 'PSL', mid: 'EICAS_INFO_CHECKLIST', label: "EICAS Information Checklist", 
        template: `
<?xml version="1.0" encoding="UTF-8"?>
<eicasInformationalChecklist>
  <title></title>

  <preamble>
    <condition>
      <para></para>
    </condition>
  </preamble>
</eicasInformationalChecklist>
`
    },
    { id: 'PSLUNANN', type: 'PSL', mid: 'UNANN_CHECKLIST', label: "Unannunciated Checklist", template: `
 <?xml version="1.0" encoding="UTF-8"?>
<unannunciatedChecklist>
  <title></title>

  <preamble>
    <condition>
      <para></para>
    </condition>

    <objective>
      <para></para>
    </objective>
  </preamble>

  <referenceTaskGroup>
    <challengeResponse>
      <challenge></challenge>

      <response></response>
    </challengeResponse>
  </referenceTaskGroup>
</unannunciatedChecklist>
`
     },

];

const TARGET_TYPES = {
    'EFB': EFB_TYPES,
    'QRH': QRH_TYPES,
};

const INCLUSIONS = [
    { value: 'RevisionHighlights', label: 'Revision Highlights' },
    { value: 'RevisionRecord', label: 'Revision Record' },
    { value: 'RevisionStatus', label: 'Revision Status' },
    { value: 'Summary', label: 'Chapter Summary List' },
    { value: 'LES', label: 'List of Effective Sections' },
    { value: 'TOC', label: 'Table of Contents' },
];

const CODE_TAGS = [
    "opsl",
    "BACK_COVER",
];

const SampleSvgIcon = (
<SvgIcon>
  {/* credit: cog icon from https://heroicons.com */}
  <svg fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M4.5 12a7.5 7.5 0 0015 0m-15 0a7.5 7.5 0 1115 0m-15 0H3m16.5 0H21m-1.5 0H12m-8.457 3.077l1.41-.513m14.095-5.13l1.41-.513M5.106 17.785l1.15-.964m11.49-9.642l1.149-.964M7.501 19.795l.75-1.3m7.5-12.99l.75-1.3m-6.063 16.658l.26-1.477m2.605-14.772l.26-1.477m0 17.726l-.26-1.477M10.698 4.614l-.26-1.477M16.5 19.794l-.75-1.299M7.5 4.205L12 12m6.894 5.785l-1.149-.964M6.256 7.178l-1.15-.964m15.352 8.864l-1.41-.513M4.954 9.435l-1.41-.514M12.002 12l-3.75 6.495"
    />
  </svg>
</SvgIcon>
);

const LockSvgIcon = (props) => {
    const {
        color,
        ...other
    } = props;

    const [fillColor, setFillColor] = useState('#ffff00');

    useEffect( () => {
        if ( color ) {
            setFillColor(color);
        }
    }, [color]);

    return (
        <SvgIcon color={color} {...other} >
            <svg x="0px" y="0px" viewBox="0 0 32 32" enable-background="new 0 0 32 32" >
                <g>
                    <path marker-start="none" marker-end="none" fill-rule="evenodd" clip-rule="evenodd" fill={fillColor} d="M6.99,19.92v-8
		c0-4.97,4.03-9,9-9c4.97,0,9,4.03,9,9l0,8c0,0.55-0.45,1-1,1h-2c-0.55,0-1-0.45-1-1l0-8c0-2.76-2.24-5-5-5c-2.76,0-5,2.24-5,5v8
		c0,0.55-0.45,1-1,1h-2C7.44,20.92,6.99,20.47,6.99,19.92z"/>
                    <path marker-start="none" marker-end="none" fill-rule="evenodd" clip-rule="evenodd" fill={fillColor} d="M4.99,30.92v-17h22v17
		H4.99z"/>
                    <rect x="15" y="20.92" fill-rule="evenodd" clip-rule="evenodd" fill="#000000" width="2" height="5" />
                    <circle fill-rule="evenodd" clip-rule="evenodd" fill="#000000" cx="16" cy="20.92" r="2" />
                </g>
            </svg>

        </SvgIcon>
    );
};

function CreateNewFile(props) {
    const {
        title = 'Create New File',
        target = 'GEN',
        open,
        onSave,
        onCancel,
        ...other
    } = props;

    const [newFileTypes, setNewFileTypes] = useState([]);
    const [fileTypeMap, setFileTypeMap] = useState({});
    const [selectedType, setSelectedType] = useState(null);
    const [selectedTypeId, setSelectedTypeId] = useState('');
    const [nameValue, setNameValue] = useState('');
    const [okEnabled, setOkEnabled] = useState(false);

    useEffect(() => {
        setNameValue('');
        setOkEnabled(false);
        setNewFileTypes([]);
        setSelectedType(null);
        setFileTypeMap({});
        setSelectedTypeId('');
        setSelectedType(null);
        if (open && target) {
            const idMap = {};
            const types = target === 'QRH' ? QRH_TYPES : EFB_TYPES;
            types.forEach(item => idMap[item.id] = item);
            setNewFileTypes(types);
            setFileTypeMap(idMap);
            if ( types.length === 1 ) {
                setSelectedTypeId(types[0].id);
                setSelectedType(types[0]);
            }
        }

    }, [open, target]);

    const onCancelClick = () => {
        onClose();
    };

    const onClose = (event, reason) => {
        if (reason === 'backdropClick') {
            return;
        }
        if (typeof onCancel === 'function') {
            onCancel();
        }
    };

    const onOKClick = () => {
        if (typeof onSave === 'function') {
            onSave({
                title: nameValue,
                spec: selectedType
            });
        }
    }

    const handleNameChange = (event) => {
        const v = event.target.value;
        setNameValue(event.target.value);
        setOkEnabled(v && v !== '' && selectedTypeId && selectedTypeId !== '');
    }

    const handleTypeChange = (event) => {
        setSelectedTypeId(event.target.value);
        setSelectedType(fileTypeMap[event.target.value]);
        setOkEnabled(nameValue && nameValue !== '');
    }

    return (
        <Dialog
            maxWidth={'900px'}
            open={open}
            onClose={onClose}
            slotProps={{
                paper: {
                    sx: {
                        minWidth: 600,
                        maxWidth: 700,
                        maxHeight: 550,
                        minHeight: 400,
                    }
                }
            }}
        >
            <DialogTitle sx={{ fontWeight: 'bold' }}>{title}</DialogTitle>
            <DialogContent>
                <TextField value={nameValue} onChange={handleNameChange} fullWidth label="Title" size="small" sx={{marginTop: 2}}/>
                <FormControl fullWidth sx={{marginTop: 3}}>
                    <InputLabel id="type-label">Type</InputLabel>
                    <Select
                        size="small"
                        labelId="type-label"
                        id="type-select"
                        value={selectedTypeId}
                        label="Type"
                        onChange={handleTypeChange}
                    >
                        {Array.isArray(newFileTypes) && newFileTypes.map(item => 
                            <MenuItem value={item.id}>{item.label}</MenuItem>
                        )}
                    </Select>
                </FormControl>
            </DialogContent>
            <DialogActions sx={{ padding: 3, paddingTop: 1}}>
                <Button variant="outlined" onClick={() => onOKClick()} sx={{ minWidth: '7em' }} disabled={!okEnabled} >Create</Button>
                <Button variant="outlined" onClick={onCancelClick} sx={{ minWidth: '7em', marginLeft: 2 }}>Cancel</Button>
            </DialogActions>
        </Dialog>

    );

}

function CreateNewDocumentNode(props) {
    const {
        title = 'Create New Document Unit',
        target = 'GEN',
        node,
        ancestors,
        schemaInfo,
        rules,
        position,
        open,
        onSave,
        onCancel,
        ...other
    } = props;

    const [newFileTypes, setNewFileTypes] = useState([]);
    const [fileTypeMap, setFileTypeMap] = useState({});
    const [selectedType, setSelectedType] = useState(null);
    const [selectedTypeId, setSelectedTypeId] = useState('');
    const [nameValue, setNameValue] = useState('');
    const [okEnabled, setOkEnabled] = useState(false);
    const [schema, setSchema] = useState([]);
    const [titleDisable, setTitleDisable] = useState(true);
    const [numberType, setNumberType] = useState('Auto');
    const [codeValue, setCodeValue] = useState('');

    const id = React.useId();

    useEffect(() => {
        setNameValue('');
        setOkEnabled(false);
        setNewFileTypes([]);
        setFileTypeMap({});
        setSelectedTypeId('');
        setSelectedType(null);
        setTitleDisable(true);
        setNumberType('Auto');
        
        setCodeValue('');
        if (open && target) {
            const idMap = {};
            const types = target === 'QRH' ? QRH_TYPES : EFB_TYPES;
            types.forEach(item => idMap[item.id] = item);
            setNewFileTypes(types);
            setFileTypeMap(idMap);
            if ( types.length === 1 ) {
                setSelectedTypeId(types[0].id);
                setSelectedType(types[0]);
            }
        }

    }, [open, target]);

    useEffect( () => {
        if ( Array.isArray(rules) ) {
            if ( rules.length === 1 ) {
                const r = rules[0];
                setSelectedType(r);
                setSelectedTypeId(r.tag);
                setTitleDisable(false);
            }
        }
    },[rules]);

    useEffect( () => {
        if ( schemaInfo ) {
            setSchema(schemaInfo);
        }
    },[schemaInfo]);

    

    const onCancelClick = () => {
        onClose();
    };

    const onClose = (event, reason) => {
        if (reason === 'backdropClick') {
            return;
        }
        if (typeof onCancel === 'function') {
            onCancel();
        }
    };

    const onOKClick = () => {
        if (typeof onSave === 'function') {
            const rule = rules.find( r => r.tag === selectedTypeId);
            onSave({
                title: nameValue,
                spec: rule,
                position: position,
                numberType: selectedType?.number ? numberType : 'Fixed',
                code: selectedType?.number ? codeValue : '-',
            });
        }
    }

    const handleNameChange = (event) => {
        const v = event.target.value;
        setNameValue(event.target.value);
        setOkEnabled(v && v !== '' && selectedTypeId && selectedTypeId !== '');
    }

    const handleTypeChange = (event) => {
        setSelectedTypeId(event.target.value);
        const r = rules.find( item => item.tag === event.target.value);
        setSelectedType(r);
        if ( event.target.value ) {
            setTitleDisable(false);
        }
        if ( event.target.value === 'description' ) {
            setTitleDisable(true);
            setNameValue('Description');
            setOkEnabled(true);
        } else {
        // setSelectedType(fileTypeMap[event.target.value]);
            setOkEnabled(nameValue && nameValue !== '');
        }
    }

    const handleNumberTypeChange = (event) => {
        setNumberType(event.target.value);
    }

    const handleCodeChange = (event) => {
        setCodeValue(event.target.value);
    }

    return (
        <Dialog
            maxWidth={'900px'}
            open={open}
            onClose={onClose}
            slotProps={{
                paper: {
                    sx: {
                        minWidth: 700,
                        maxWidth: 700,
                        maxHeight: 550,
                        minHeight: 400,
                    }
                }
            }}
        >
            <DialogTitle sx={{ fontWeight: 'bold' }}>{title}</DialogTitle>
            <DialogContent>
                <FormControl fullWidth sx={{marginTop: 3}}>
                    <InputLabel id="type-label">Type</InputLabel>
                    <Select
                        size="small"
                        labelId="type-label"
                        id="type-select"
                        value={selectedTypeId}
                        label="Type"
                        onChange={handleTypeChange}
                    >
                        {Array.isArray(rules) && rules.sort((a,b) => a.label?.localeCompare(b.label)).map(item => 
                            <MenuItem value={item.tag}>{item.label}</MenuItem>
                        )}
                    </Select>
                </FormControl>
                <TextField value={nameValue} onChange={handleNameChange} fullWidth label="Title" size="small" sx={{marginTop: 4, visibility: titleDisable ? 'hidden' : 'visible'}}/>
                {selectedType?.number &&
                    <>
                        <FormControl sx={{ display: 'flex', mt: 3, flexDirection: 'row', alignItems: 'center' }}>
                            <FormLabel id={`${id}-ntype-label`} sx={{ width: '9em', color: 'text.primary' }}>Number Type</FormLabel>
                            <RadioGroup
                                row
                                aria-labelledby={`${id}-ntype-label`}
                                value={numberType}
                                onChange={handleNumberTypeChange}
                                name="ntype-radio-buttons-group"
                            >
                                <FormControlLabel value="Auto" control={<Radio />} label="Auto" />
                                <FormControlLabel value="Fixed" control={<Radio />} label="Fixed" />
                            </RadioGroup>
                        </FormControl>
                        <TextField value={codeValue} onChange={handleCodeChange} fullWidth label="Number" size="small" 
                            sx={{ marginTop: 3, visibility: numberType === 'Fixed' ? 'visible' : 'hidden' }} />
                    </>
                }
            </DialogContent>
            <DialogActions sx={{ padding: 3, paddingTop: 1}}>
                <Button variant="outlined" onClick={() => onOKClick()} sx={{ minWidth: '7em' }} disabled={!okEnabled} >Insert</Button>
                <Button variant="outlined" onClick={onCancelClick} sx={{ minWidth: '7em', marginLeft: 2 }}>Cancel</Button>
            </DialogActions>
        </Dialog>

    );

}


function EditDocumentNode(props) {
    const {
        title = 'Edit Document Node',
        target = 'GEN',
        node,
        open,
        onSave,
        onCancel,
        ...other
    } = props;

    const [selectedType, setSelectedType] = useState(null);
    const [selectedTypeId, setSelectedTypeId] = useState('');
    const [nameValue, setNameValue] = useState('');
    const [okEnabled, setOkEnabled] = useState(false);
    const [schema, setSchema] = useState([]);
    const [titleDisable, setTitleDisable] = useState(true);
    const [numberType, setNumberType] = useState('Auto');
    const [codeValue, setCodeValue] = useState('');

    const id = React.useId();

    useEffect(() => {
        setNameValue('');
        setOkEnabled(false);
        // setNewFileTypes([]);
        // setFileTypeMap({});
        setTitleDisable(false);
        setNumberType('Auto');
        setCodeValue('');
        if (open && node) {
            if ( node.entry?.attributeMap?.numberType ) {
                const nt = node.entry.attributeMap.numberType;
                setNumberType(nt === 'Inherit' ? 'Auto' : nt);
                setCodeValue(node.entry?.code ? node.entry.code : '');
                if ( node.label ) {
                    setNameValue(node.label);
                }
            }
        }

    }, [open, node]);


    const onCancelClick = () => {
        onClose();
    };

    const onClose = (event, reason) => {
        if (reason === 'backdropClick') {
            return;
        }
        if (typeof onCancel === 'function') {
            onCancel();
        }
    };

    const onOKClick = () => {
        if (typeof onSave === 'function') {
            onSave({
                title: nameValue,
                entry: node.entry,
                numberType:  numberType ,
                code: codeValue,
            });
        }
    }

    const handleNameChange = (event) => {
        const v = event.target.value;
        setNameValue(event.target.value);
        setOkEnabled(v && v !== '');
    }

    const handleNumberTypeChange = (event) => {
        setNumberType(event.target.value);
        setOkEnabled(true);
    }

    const handleCodeChange = (event) => {
        setCodeValue(event.target.value);
        setOkEnabled(true);
    }

    return (
        <Dialog
            maxWidth={'900px'}
            open={open}
            onClose={onClose}
            slotProps={{
                paper: {
                    sx: {
                        minWidth: 700,
                        maxWidth: 700,
                        maxHeight: 550,
                        minHeight: 400,
                    }
                }
            }}
        >
            <DialogTitle sx={{ fontWeight: 'bold' }}>{title}</DialogTitle>
            <DialogContent>
                <TextField value={nameValue} onChange={handleNameChange} fullWidth label="Title" size="small" sx={{marginTop: 4, visibility: titleDisable ? 'hidden' : 'visible'}}/>
                {node?.entry?.mid && node?.entry?.mid !== 'ipsl' &&
                    <>
                        <FormControl sx={{ display: 'flex', mt: 3, flexDirection: 'row', alignItems: 'center' }}>
                            <FormLabel id={`${id}-ntype-label`} sx={{ width: '9em', color: 'text.primary' }}>Number Type</FormLabel>
                            <RadioGroup
                                row
                                aria-labelledby={`${id}-ntype-label`}
                                value={numberType}
                                onChange={handleNumberTypeChange}
                                name="ntype-radio-buttons-group"
                            >
                                <FormControlLabel value="Auto" control={<Radio />} label="Auto" />
                                <FormControlLabel value="Fixed" control={<Radio />} label="Fixed" />
                            </RadioGroup>
                        </FormControl>
                        <TextField value={codeValue} onChange={handleCodeChange} fullWidth label="Number" size="small" 
                            sx={{ marginTop: 3, visibility: numberType === 'Fixed' ? 'visible' : 'hidden' }} />
                    </>
                }
            </DialogContent>
            <DialogActions sx={{ padding: 3, paddingTop: 1}}>
                <Button variant="outlined" onClick={() => onOKClick()} sx={{ minWidth: '7em' }} disabled={!okEnabled} >Save</Button>
                <Button variant="outlined" onClick={onCancelClick} sx={{ minWidth: '7em', marginLeft: 2 }}>Cancel</Button>
            </DialogActions>
        </Dialog>

    );

}

function DeleteFileDialog(props) {
    const {
        title = 'Delete Document Node',
        target = 'GEN',
        name,
        open,
        onSave,
        onCancel,
        ...other
    } = props;

     const [okEnabled, setOkEnabled] = useState(true);

     /*
    useEffect(() => {
        if (open && target) {
            const idMap = {};
            const types = target === 'QRH' ? QRH_TYPES : EFB_TYPES;
            types.forEach(item => idMap[item.id] = item);
            setNewFileTypes(types);
            setFileTypeMap(idMap);
            if ( types.length === 1 ) {
                setSelectedTypeId(types[0].id);
                setSelectedType(types[0]);
            }
        }

    }, [open, target]);
    */

    const onCancelClick = () => {
        onClose();
    };

    const onClose = (event, reason) => {
        if (reason === 'backdropClick') {
            return;
        }
        if (typeof onCancel === 'function') {
            onCancel();
        }
    };

    const onOKClick = () => {
        if (typeof onSave === 'function') {
            onSave({
                title: name,
            });
        }
    }

    return (
        <Dialog
            maxWidth={'900px'}
            open={open}
            onClose={onClose}
            slotProps={{
                paper: {
                    sx: {
                        minWidth: 600,
                        maxWidth: 700,
                        maxHeight: 650,
                        minHeight: 300,
                    }
                }
            }}
        >
            <DialogTitle sx={{ fontWeight: 'bold' }}>{title}</DialogTitle>
            <DialogContent>
                 <Typography sx={{paddingTop: 3, fontSize: '110%', fontStyle: 'italic'}}>{`Do you want to delete document node "${name}"?`}</Typography>
            </DialogContent>
            <DialogActions sx={{ padding: 3, paddingTop: 1}}>
                <Button variant="outlined" onClick={() => onOKClick()} sx={{ minWidth: '7em' }} disabled={!okEnabled} color="error">Delete</Button>
                <Button variant="outlined" onClick={onCancelClick} sx={{ minWidth: '7em', marginLeft: 2 }}>Cancel</Button>
            </DialogActions>
        </Dialog>

    );

}

function CheckinDialog(props) {
    const {
        title = 'Checkin Document Unit',
        open,
        node,
        onSave,
        onCancel,
        ...other
    } = props;
    const [message, setMessage] = React.useState('');


    const onOKClick = () => {
        if (typeof onSave === 'function') {
            onSave(message, node);
        }
    }

    const onCancelClick = () => {
        onClose();
    };

    const onClose = () => {
        if (typeof onCancel === 'function') {
            onCancel();
        }
    };

    const messageChanged = (event) => {
        setMessage(event.target.value);
    }


    return (
        <Dialog
            maxWidth={'900px'}
            open={open}
            onClose={onClose}
            slotProps={{
                paper: {
                    sx: {
                        minWidth: 600,
                        maxWidth: 600,
                        maxHeight: 320,
                        minHeight: 320,
                    }                   
                }
            }}
        >
            <DialogTitle sx={{ fontWeight: 'bold' }}>{title}</DialogTitle>
            <DialogContent>

                <Box sx={{ paddingTop: '2ex' }}>
                    <TextField label="Checkin message" value={message} onChange={messageChanged} size="small" fullWidth autoFocus={true}
                        multiline rows={4} />
                </Box>

            </DialogContent>
            <DialogActions sx={{padding: 4, paddingTop: 0}}>
                <Button variant="outlined" onClick={onOKClick} sx={{ minWidth: '7em' }} >Checkin</Button>
                <Button variant="outlined" onClick={onCancelClick} sx={{ minWidth: '7em', ml: 4 }}>Cancel</Button>
            </DialogActions>
        </Dialog>

    );

}


var globalDocumentModel;

function ChooseDocumentUnitDialog(props) {
    const {
        title = 'Edit Document',
        instance = null,
        mid = 'chapter,FRONTMATTER',
        target = 'GEN',
        document,
        action,
        actionIndex,
        open,
        onSave,
        onCancel,
        ...other
    } = props;
    const [model, setModel] = useState([]);
    const [selectedNode, setSelectedNode] = useState(null);
    const [ancestors, setAncestors] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    const [expandedItems, setExpandedItems] = useState([]);
    const [okEnabled, setOkEnabled] = useState(false);
    const [newFileEnabled, setNewFileEnabled] = useState(false);
    const [newFolderEnabled, setNewFolderEnabled] = useState(false);
    const [deleteFileEnabled, setDeleteFileEnabled] = useState(false);
    const [newFileDialogOpen, setNewFileDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [newFolderDialogOpen, setNewFolderDialogOpen] = useState(false);
    const [deleteFileDialogOpen, setDeleteFileDialogOpen] = useState(false);
    const [checkinDialogOpen, setCheckinDialogOpen] = useState(false);
    const [contextMenu, setContextMenu] = React.useState(null);
    const [menuIndex, setMenuIndex] = React.useState(1);
    const [insertIntoDisable, setInsertIntoDisable] = useState(false);
    const [insertBeforeDisable, setInsertBeforeDisable] = useState(false);
    const [insertAfterDisable, setInsertAfterDisable] = useState(false);
    const [editNodeDisable, setEditNodeDisable] = useState(false);
    const [checkoutEnabled, setCheckoutEnabled] = useState(false);
    const [checkinEnabled, setCheckinEnabled] = useState(false);
    const [schema, setSchema] = useState([]);
    const [schemaRule, setSchemaRule] = useState(null);
    const [position, setPosition] = useState('INTO');
    // const serverMessage = useSelector((state) => state.message.payload);

    const mrid = React.useId();
    const menuId = `${mrid}-menu`;

    const queue = new PQueue({concurrency: 1});

    useEffect( () => {
        console.log('Subscribing to postal websocket.message: ' );
        /*
        messageSubscribe(
            'websocket.message',
            'choose_docunit_dialog',
            (payload) => {
                console.log('Got server message in document: ' + JSON.stringify(payload) );
                if ( payload.type === 'document'  ) {
                    handlePostalServerMessage(payload.data);
                }
            }
        );
        */
        const messageUnsub = postal.subscribe(
            'websocket.message',
            envelope => {
                console.log('Got server message in document: ' + JSON.stringify(envelope.payload) );
                if ( envelope.payload.type === 'document'  ) {
                    handlePostalServerMessage(envelope.payload.data);
                }
            }
        )
        
        return () => {
            // messageUnsubscribe('websocket.message','choose_docunit_dialog');
            messageUnsub();
        }
        

    },[]);

    useEffect( () => {
        globalDocumentModel = [...model];
    }, [model]);

    useEffect(() => {
        setNewFileEnabled(false);
        setNewFolderEnabled(false);
        setDeleteFileEnabled(false);
        setSelectedItems(null);
        if (open && document) {
            /*
            let url = '/api/doc/tree/' + encodeURIComponent(document) + '?type=DU&mid=' + encodeURIComponent(mid) +
                '&target=' + encodeURIComponent(target);
            console.log('docUnit load ' + url);
            netGet(url)
                .then(response => response.json())
                .then(droot => {

                    if (droot) {
                        setModel([droot]);
                        setExpandedItems([droot.id]);
                        setSelectedItems(null);

                    }
                }).catch(error => {
                    console.log('Error fetching Document tree: ' + error);
                });
            */
            loadDoc(document,mid,target);
        } else {
            setModel([]);
            setExpandedItems([]);
            setSelectedItems(null);
        }
    }, [open, document])

    const handlePostalServerMessage = (message) => {
        
        if ( message.category === 'Document') {
            // console.log('Received document message: ' + JSON.stringify(message));
            if ( message.eid ) {
                if ( message.action === 'CREATE' || message.action === 'DELETE' ) {
                    queue.add(async () => {
                        await loadDoc();
                    });
                } else if ( message.action === 'DOC_CREATE' || message.action === 'DOC_DELETE') {
                    // do nothing
                } else {
                    queue.add(async () => {
                        const uri = '/api/doc/entry/get/' + message.eid;
                        console.log('Getting entry : ' + uri)
                        await netGet(uri)
                            .then(resp => resp.json())
                            .then(node => {
                                if (node) {
                                    // console.log('Got Message Node: ' + node.id);
                                    const items = globalDocumentModel;
                                    const ref = findItemRef(node.id, items);
                                    if (ref) {
                                        // console.log('Found tree item: ' + ref.index);
                                        ref.items[ref.index].entry = node.entry;
                                        setModel(items);
                                    } else {
                                        console.log('Item NOT FOUND: ' + node.id + ' in ' + items.length);
                                    }
                                }
                            }).catch(error => {
                                console.log('Error fetching node: ' + error);
                            });
                    });
                }
            }
        }
    };

    const findItemRef = (id, items = model) => {
        if (Array.isArray(items)) {
            for (let i = 0; i < items.length; i++) {
                const item = items[i];
                if (id === item.id) {
                    return { items: items, index: i};
                }
                const child = findItemRef(id, item.children);
                if (child) {
                    return child;
                }
            }
        }
        return undefined;
    }

    const loadDoc = async (doc,mmid,dtarget,itemId) => {
        let url = '/api/doc/tree/' + encodeURIComponent(doc) + '?type=DU&mid=' + encodeURIComponent(mmid) +
            '&target=' + encodeURIComponent(dtarget);
        // console.log('docUnit load2 ' + url);
        try {
            const response = await netGet(url);
            const droot = await response.json();
            if (droot) {
                const mod = [droot];
                setModel(mod);
                if (!Array.isArray(expandedItems) || expandedItems.length === 0) {
                    setExpandedItems([droot.id]);
                }
                if (itemId) {
                    handleItemClick(null, itemId, mod);
                } else {
                    setSelectedItems(null);
                }

            }
            netGet('/api/doc/schema/info/' + encodeURIComponent(doc))
                .then(response_1 => response_1.json())
                .then(schema => {
                    if (schema?.cloudViewSchema) {
                        // console.log('SCHEMA: ' + JSON.stringify(schema.cloudViewSchema));
                        setSchema(schema.cloudViewSchema);
                    }
                });
        } catch (error) {
            console.log('Error fetching Document tree: ' + error);
        }
    };

    const onOKClick = () => {
        if (typeof onSave === 'function') {
            const entry = selectedNode?.entry ? selectedNode.entry : {};
            const asc = [];
            getAncestors(asc, model, selectedNode);
            // add the document as top node for compatibility
            if (!asc.includes(document)) {
                asc.push(document);
            }
            const du = {
                id: entry.id,
                targetProfile: target,
                entryId: entry.entryId,
                path: selectedNode.rootPath + '/' + (entry.contentPath ? entry.contentPath : ''),
                title: selectedNode.label,
                prefix: selectedNode.prefix ? selectedNode.prefix + ' ' : '', // entry.code && entry.code !== '' && entry.code !== '-' ? entry.code + ' ' : '',
                node: selectedNode,
                procId: instance?.id,
                ancestors: asc,
            };
            // console.log('DU: ' + JSON.stringify(du)); 
            onSave(du);
        }
    }


    const onCancelClick = () => {
        onClose();
    };

    const onClose = (event, reason) => {
        if (reason === 'backdropClick') {
            return;
        }
        if (typeof onCancel === 'function') {
            onCancel();
        }
    };

    const handleNewFile = () => {
        setNewFileDialogOpen(true);
    };

    const handleNewFileSave = (entry) => {
        
        setNewFileDialogOpen(false);
        const pentry = selectedNode?.entry ? selectedNode.entry : {};
        // console.log('Context Node: ' + JSON.stringify(pentry));
        const info = {
            pid: pentry.entryId,
            title: entry.title,
            type: entry.spec.type,
            mid: entry.spec.tag,
            docId: document,
            position: entry.position,
            profile: target,
            numberType: entry.numberType,
            code: entry.code,
        };
        
        console.log('new file: ' + JSON.stringify(info));
        
        netPost('/api/doc/entry/new', info)
            .then(response => response.json())
            .then(entry => {
                // setModel([]);
                let itemId;
                if (entry) {
                    itemId = entry.id;
                }
                // setTimeout(() => loadDoc(document, mid, target, itemId), 100);
                loadDoc(document, mid, target, itemId);
            }).catch(error => {
                console.log('Error creating file: ' + error);
            });
        
    }

    const handleNewFolder = () => {
        setNewFolderDialogOpen(true);
    };

    const handleNewFolderSave = (entry) => {
        setNewFolderDialogOpen(false);
    }

    const handleSelectedItemsChange = (event, ids) => {
        // setSelectedItems(ids);
        // console.log('Selected items: ' + JSON.stringify(ids));
        // onSelectedItemsChange={handleSelectedItemsChange}
    };

    

    const getAncestors = (asc, items, target) => {
        /*
        if ( target?.entry?.id ) {
            asc.push(target.entry.id);
        }
        */
        const id = target?.id;
        if (Array.isArray(items)) {
            for (let i = 0; i < items.length; i++) {
                const item = items[i];
                if (id === item.id) {
                    asc.push(id);
                    return true;
                }
                const hasItem = getAncestors(asc, item.children, target);
                if (hasItem) {
                    asc.push(item.id);
                    return true;
                }
            }
        }
        return false;
    }

    const findItem = (items, id) => {
        if (Array.isArray(items)) {
            for (let i = 0; i < items.length; i++) {
                const item = items[i];
                if (id === item.id) {
                    return item;
                }
                const child = findItem(item.children, id);
                if (child) {
                    return child;
                }
            }
        }
        return undefined;
    }

    

    const handleItemClick = (event, itemId, mod=model) => {
        console.log('itemClick: ' + JSON.stringify(itemId));
        /*
        if (itemId === selectedItems) {
            setSelectedItems(null);
            setOkEnabled(false);
            setSelectedNode(null);
            setNewFileEnabled(false);
            setNewFolderEnabled(false);
            setDeleteFileEnabled(false);
        } else {
        */
            const item = findItem(mod, itemId);
            console.log('Selected: ' + JSON.stringify(item.id));
            // if ( item?.entry.type === 'DU' ) {
            if (item?.type === 'DU') {
                setSelectedItems(itemId);
                setOkEnabled(true);
                setSelectedNode(item);
                setNewFileEnabled(true);
                setNewFolderEnabled(false);
                setDeleteFileEnabled(true);
                findAncestors(itemId);
            } else {
                setSelectedItems(null);
                setSelectedNode(null);
                setOkEnabled(false);
                if (item) {
                    // setNewFileEnabled(item.entry?.mid !== 'VOLUME');
                    setNewFileEnabled(true);
                    setNewFolderEnabled(true);
                    setDeleteFileEnabled(item.entry?.mid !== 'VOLUME');
                    setSelectedNode(item);
                    setSelectedItems(itemId);
                    findAncestors(itemId);
                } else {
                    setNewFileEnabled(false);
                    setNewFolderEnabled(false);
                    setDeleteFileEnabled(false);
                }
            }

        // }
    }

    const findAncestors = (itemId) => {
        const item = findItem(model, itemId);
        const asc = [];
        let pid = item?.parentId;
        while ( pid ) {
            const pitem = findItem(model, pid);
            if (pitem ) {
                const pi = {...pitem};
                pi.children = null;
                asc.push(pi);
                pid = pitem.parentId;
            } else {
                break;
            }
        }
        // console.log('Ancestors: ' + JSON.stringify(asc));
        // setAncestors(asc);
        return asc;
    };

    const matchCodeRule = (tag, code) => {
        // first trying matching the code 
        console.log('Matching code: ' + tag + ':' + code );
        let rule = schema.find(r => {
            const c = r.code ? r.code.replace(/\s+/,' ') : '';
            const rtag = Array.isArray(r.tag) ? r.tag : [r.tag];
            if (rtag.includes(tag) && code === c) {
                return true;
            }
            return false;
        });
        /*
        if (!rule) {
            // otherwise match only tag
            rule = schema.find(r => {
                if (r.tag === tag && !r.code && !r.doctype && !r.asc) {
                    return true;
                }
                return false;
            });
        }
        */
        return rule;
    };

    const matchTagRule = (tag) => {
        const rule = schema.find(r => {
            const rtag = Array.isArray(r.tag) ? r.tag : [r.tag];
            if (rtag.includes(tag) && !r.code && !r.doctype && !r.asc && !r.acode ) {
                return true;
            }
            return false;
        });
        return rule;
    };

    const matchAscRule = (tag, asc) => {
        let rule = schema.find(r => {
            const c = r.asc ? r.asc : '';
            const rtag = Array.isArray(r.tag) ? r.tag : [r.tag];
            if (rtag.includes(tag) && asc === c) {
                return true;
            }
            return false;
        });
        return rule;
    };

    const matchAscCodeRule = (tag, acode) => {
        console.log('Matching ASC code: ' + tag + ':' + acode );
        let rule = schema.find(r => {
            const c = r.acode ? r.acode : '';
            const rtag = Array.isArray(r.tag) ? r.tag : [r.tag];
            if (rtag.includes(tag) && acode && acode !== '' && acode === c) {
                return true;
            }
            return false;
        });
        return rule;
    };

    const matchRule = (node) => {
        const tag = node.entry?.mid;
        const code = CODE_TAGS.includes(tag) ? node.entry.code.replace(/\s+/,' ') : '';
        let rule;
        if ( code !== '' ) {
            rule = matchCodeRule(tag, code);
            if ( rule ) {
                console.log('Found CODE rule');
            }
        }
        if ( !rule ) {
            const asc = findAncestors(node.id);
            if ( Array.isArray(asc) && asc.length > 0 ) {
                for (let i = 0; i < asc.length; i++) {
                    const pnode = asc[i];
                    const ptag = pnode.entry?.mid;
                    const pcode = CODE_TAGS.includes(ptag) ? pnode.entry.code : '';
                    rule = matchAscCodeRule(tag, pcode);
                    if (rule) {
                        console.log('Found ASC CODE rule');
                        break;
                    }
                }
                if (!rule) {
                    for (let i = 0; i < asc.length; i++) {
                        const pnode = asc[i];
                        const ptag = pnode.entry?.mid;
                        rule = matchAscRule(tag, ptag);
                        if (rule) {
                            console.log('Found ASC rule');
                            break;
                        }
                    }
                }
                /*
                if (!rule) {
                    for (let i = 0; i < asc.length; i++) {
                        const pnode = asc[i];
                        const ptag = pnode.entry?.mid;
                        const pcode = CODE_TAGS.includes(ptag) ? pnode.entry.code : '';
                        rule = matchCodeRule(tag, pcode);
                        if (rule) {
                            break;
                        }
                    }
                }
                */
            }
        }
        if (!rule) {
            rule = matchTagRule(tag);
            if ( rule ) {
                console.log('Found TAG rule');
            }
        }
        return rule;
    };

    const matchDoctypeRule = (tag, doctype) => {
        let rule = schema.find(r => {
            const c = r.doctype ? r.doctype : '';
            if (r.tag === tag && c !== '' && doctype.includes(c)) {
                return true;
            }
            return false;
        });
        return rule;
    };
    

    const findRuleValue = (node, type) => {
        const tag = node.entry?.mid;
        const code = CODE_TAGS.includes(tag) ? node.entry.code : '';
        const doctype = node.doctype ? node.doctype : '';
        const rule = matchRule(node); //  matchCodeRule(tag,code);
        const vv = [];
        if (rule) {
            console.log('FOUND rule [' + type + ']: ' + JSON.stringify(rule));
            switch (type) {
                case 'INTO':
                    if ( Array.isArray(rule.into) ) {
                        rule.into.forEach( e => {
                            if ( !e.mgmt ) {
                                vv.push(e);
                            }
                        });
                    }
                    break;
                case 'BEFORE':
                    let dfound = false;
                    if ( doctype !== '') {
                        const drule = matchDoctypeRule(tag, doctype);
                        if (Array.isArray(drule?.before)) {
                            dfound = true;
                            drule.before.forEach( e => {
                            if ( !e.mgmt ) {
                                vv.push(e);
                            }
                        });
                        }
                    }
                    if ( !dfound && Array.isArray(rule.before) ) {
                        rule.before.forEach( e => {
                            if ( !e.mgmt ) {
                                vv.push(e);
                            }
                        });
                    }
                    break;
                case 'AFTER':
                    let afound = false;
                    if ( doctype !== '') {
                        const drule = matchDoctypeRule(tag, doctype);
                        if (Array.isArray(drule?.after)) {
                            afound = true;
                            drule.after.forEach( e => {
                            if ( !e.mgmt ) {
                                vv.push(e);
                            }
                        });
                        }
                    }
                    if (!afound && Array.isArray(rule.after) ) {
                        rule.after.forEach( e => {
                            if ( !e.mgmt ) {
                                vv.push(e);
                            }
                        });
                    }
                    break;

            }
        }
        return vv;
    };

    const handleContextMenuClick = (event, itemId) => {
        // console.log('itemId: ' + JSON.stringify(itemId));
        // handleItemClick(event, itemId);
        event.preventDefault();
        event.stopPropagation();
        
        const item = findItem(model, itemId);
        let dinto = true;
        if ( item?.type === 'DU' ) {
            setInsertIntoDisable(true);
            setEditNodeDisable(true);
            dinto = true;
            setOkEnabled(true);
            const locked = typeof item?.entry?.lock === 'string' && item.entry.lock.length > 0;
            setCheckoutEnabled(!locked);
            const ci = item?.entry?.lock === UserInfo.info.name;
            setCheckinEnabled(ci);
            // console.log('Checkin enabled = ' + ci);
        } else {
            setInsertIntoDisable(false);
            setEditNodeDisable(false);
            setOkEnabled(false);
            setCheckoutEnabled(false);
            setCheckinEnabled(false);
            dinto = false;
        }
        if (item) {
            setSelectedNode(item);
            let options = findRuleValue(item, 'INTO');
            // console.log('Options: '  + JSON.stringify(options));
            if ( dinto || !(Array.isArray(options) && options.length > 0) ) {
                setInsertIntoDisable(true);
            } else { 
                setInsertIntoDisable(false);
            }
            options = findRuleValue(item, 'BEFORE');
            setInsertBeforeDisable(!(Array.isArray(options) && options.length > 0));
            options = findRuleValue(item, 'AFTER');
            setInsertAfterDisable(!(Array.isArray(options) && options.length > 0))
        }
        
        if (itemId !== selectedItems) {
            setSelectedItems(itemId);
        }
        
        console.log('item type = ' + item?.type);
        setContextMenu(
            contextMenu === null
                ? {
                    mouseX: event.clientX + 2,
                    mouseY: event.clientY - 6,
                }
                : // repeated contextmenu when it is already open closes it with Chrome 84 on Ubuntu
                // Other native context menus might behave different.
                // With this behavior we prevent contextmenu from the backdrop to re-locale existing context menus.
                null,
        );
    };

    const handleInsertInto = (event) => {
        handleContextMenuClose();
        const item = selectedNode;
        if ( item ) {
            const options = findRuleValue(item, 'INTO');
            setSchemaRule(options);
            setPosition('INTO');
            setNewFileDialogOpen(true);
        }
    };

    const handleInsertBefore = (event) => {
        handleContextMenuClose();
        const item = selectedNode;
        if ( item ) {
            const options = findRuleValue(item, 'BEFORE');
            setSchemaRule(options);
            setPosition('BEFORE');
            setNewFileDialogOpen(true);
        }   
    };

    const handleInsertAfter = (event) => {
        handleContextMenuClose();
        const item = selectedNode;
        if ( item ) {
            const options = findRuleValue(item, 'AFTER');
            setSchemaRule(options);
            setPosition('AFTER');
            setNewFileDialogOpen(true);
        }   
    };

    const handleCheckoutClick = (event) => {
        handleContextMenuClose();
        const item = selectedNode;
        if ( item ) {
            if (!item.entry?.lock) {
                const pentry = selectedNode?.entry ? selectedNode.entry : {};
                // console.log('parent: ' + JSON.stringify(pentry));
                const info = {
                    eid: pentry.entryId,
                    docId: document,
                    entry: {...pentry},
                };
                // console.log('delete file: ' + JSON.stringify(info));

                netPost('/api/doc/entry/checkout', info).then(response => {
                    if (!response.ok) {
                        console.log('Could not checkout file: error = ' + response.status);
                    } else {
                        console.log('Checked out file ' + pentry.entryId);
                        /*
                        setModel([]);
                        setTimeout(() => loadDoc(document, mid, target), 100);
                        */
                        // setOkEnabled(false);
                        // loadDoc(document, mid, target);
                    }
                });
            }
        }
    };

    const handleCheckinClick = (event) => {
        handleContextMenuClose();
        setCheckinDialogOpen(true);
    };

    const handleCheckinSave = (message, item) => {
        handleContextMenuClose();
        setCheckinDialogOpen(false);
        if ( item ) {
            if (item.entry?.lock === UserInfo.info.name ) {
                const pentry = selectedNode?.entry ? selectedNode.entry : {};
                // console.log('parent: ' + JSON.stringify(pentry));
                const info = {
                    eid: pentry.entryId,
                    docId: document,
                    entry: {...pentry},
                    message: message,
                };
                // console.log('delete file: ' + JSON.stringify(info));

                netPost('/api/doc/entry/checkin', info).then(response => {
                    if (!response.ok) {
                        console.log('Could not checkin file: error = ' + response.status);
                    } else {
                        console.log('Checkin file ' + pentry.entryId);
                        /*
                        setModel([]);
                        setTimeout(() => loadDoc(document, mid, target), 100);
                        */
                        // setOkEnabled(false);
                        // loadDoc(document, mid, target);
                        postal.publish(
                            "xmleditor.checkin.event",
                            {
                                eid: pentry.entryId,
                                id: pentry.id,
                                docId: document,
                                value: {action: action, index: actionIndex, wid: instance?.id, sid: instance.stepId}
                            }
                        );
                    }
                });
            }
        }
    };

    const handleRevokeCheckoutClick = (event) => {
        handleContextMenuClose();
        const item = selectedNode;
        if ( item ) {
            if (item.entry?.lock === UserInfo.info.name ) {
                const pentry = selectedNode?.entry ? selectedNode.entry : {};
                // console.log('parent: ' + JSON.stringify(pentry));
                const info = {
                    eid: pentry.entryId,
                    docId: document,
                    entry: {...pentry},
                };
                // console.log('delete file: ' + JSON.stringify(info));

                netPost('/api/doc/entry/revoke', info).then(response => {
                    if (!response.ok) {
                        console.log('Could not revoke checkout file: error = ' + response.status);
                    } else {
                        console.log('Revoke checked out file ' + pentry.entryId);
                        /*
                        setModel([]);
                        setTimeout(() => loadDoc(document, mid, target), 100);
                        */
                        // setOkEnabled(false);
                        // loadDoc(document, mid, target);
                    }
                });
            }
        }
    };

    const handleTopContextClick = (event) => {
        event.preventDefault();
        handleContextMenuClose();
    };

    const handleContextMenuClose = () => {
        setContextMenu(null);
        setMenuIndex(-1);
    };

    const handleDeleteFileClick = () => {
        handleContextMenuClose();
        setDeleteFileDialogOpen(true);
    };

    const handleDeleteFileDialogSave = () => {
        handleContextMenuClose();
        setDeleteFileDialogOpen(false);
        const pentry = selectedNode?.entry ? selectedNode.entry : {};
        // console.log('parent: ' + JSON.stringify(pentry));
        const info = {
            pid: pentry.entryId,
            docId: document,
        };
        // console.log('delete file: ' + JSON.stringify(info));
        
        netPost('/api/doc/entry/del', info).then(response => {
            if (!response.ok) {
                console.log('Could not delete file: error = ' + response.status);
            } else {
                /*
                setModel([]);
                setTimeout(() => loadDoc(document, mid, target), 100);
                */
                setOkEnabled(false);
                loadDoc(document, mid, target);
            }
        });
        
    };

    const handleEditDocumentNode = () => {
        setEditDialogOpen(true);
    };

    const handleEditDocumentNodeSave = (data) => {
        setEditDialogOpen(false);
        handleContextMenuClose();
        const info = {
            pid: data.entry.entryId,
            docId: document,
            title: data.title,
            numberType: data.numberType,
            code: data.code,
        };
        netPost('/api/doc/entry/update', info).then(response => {
            if (!response.ok) {
                console.log('Could not update node: error = ' + response.status);
            } else {
                /*
                setModel([]);
                setTimeout(() => loadDoc(document, mid, target), 100);
                */
                loadDoc(document, mid, target);
            }
        });
    };

    const displayNode = (item) => {
        // const prefix = item.entry?.mid !== 'VOLUME' && item.entry?.code && item.entry?.code !== '' && item.entry?.code !== '-' ? item.entry.code + ' ' : '';
        const prefix = item.entry?.mid !== 'VOLUME' && item.prefix ? item.prefix + ' ' : '';
        return (<TreeItem itemId={item.id} label={prefix + item.label} onContextMenu={(event) => handleContextMenuClick(event, item.id)} >
            {Array.isArray(item.children) && item.children.length > 0 &&
                item.children.map(child => displayNode(child))
            }
        </TreeItem>);
    };

    const CustomTreeItem = memo(({ node, onUpdateNode }) => {
        // If this specific node needs local edits (like renaming or dynamic toggles)
        const [label, setLabel] = useState(node.label);
        const [prefix, setPrefix] = useState(node.entry?.mid !== 'VOLUME' && node.prefix ? node.prefix + ' ' : '');
        const [type, setType] = useState(node.type);
        const [lock, setLock] = useState(node.entry?.lock);
        const [lockCount, setLockCount] = useState(node.entry?.lockCount);

        const getLockColor = (lock) => {
            return ( lock === UserInfo.info.name ? '#00cc00' : '#e60000') 
        }

        return (
            <TreeItem
                itemId={node.id}
                label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {type === 'DU' ?
                            <SubjectIcon fontSize="small" /> :
                            <FolderOpenIcon fontSize="small" />
                        }
                        { type === 'DU' && lock &&
                            <Tooltip title={`Locked by ${lock}`}>
                                <LockSvgIcon color={getLockColor(lock)} sx={{position: 'relative', left: '-18px', top: '6px', fontSize: '12px'}} />
                            </Tooltip>
                        }
                        { type !== 'DU' && lockCount > 0 &&
                            <Tooltip title="Child items are checked out">
                                <LockSvgIcon color="#e0e01a" sx={{position: 'relative', left: '-30px', top: '-6px', fontSize: '12px'}} />
                            </Tooltip>
                        }

                        <Typography sx={{position: 'relative', left: lock || lockCount > 0 ? '-18px' : '0px'}}>{`${prefix} ${label}`}</Typography>

                        {/* <Typography>{`${prefix} ${label}`}</Typography> */}
                    </Box>
                }
                onContextMenu={(event) => handleContextMenuClick(event, node.id)}
            >
                {/* 2. Map children efficiently */}
                {node.children?.map((child) => (
                    <CustomTreeItem key={child.id} node={child} onUpdateNode={onUpdateNode} />
                ))}
            </TreeItem>
        );
    });

    return (
        <Dialog
            maxWidth={'900px'}
            open={open}
            onClose={onClose}
            slotProps={{
                paper: {
                    sx: {
                        minWidth: 800,
                        maxWidth: 900,
                        maxHeight: 750,
                        minHeight: 700,
                    }
                }
            }}
        >
            <DialogTitle sx={{ fontWeight: 'bold' }}>{title}</DialogTitle>
            <DialogContent>
                <Box sx={{ height: '100%' }}>
                    <SimpleTreeView
                        selectedItems={selectedItems}
                        onItemClick={handleItemClick}
                        expansionTrigger="iconContainer"
                        expandedItems={expandedItems}
                        onExpandedItemsChange={(event, itemids) => setExpandedItems(itemids)}
                        onContextMenu={handleTopContextClick}
                    >
                        {/* Array.isArray(model) && model.length > 0 &&
                            model.map(item => displayNode(item))
                        */}
                        { Array.isArray(model) && model.length > 0 &&
                            model.map(item => <CustomTreeItem key={item.id} node={item} />)
                        }
                    </SimpleTreeView>
                </Box>
                <Menu
                    id={menuId}
                    open={contextMenu !== null}
                    onClose={handleContextMenuClose}
                    anchorReference="anchorPosition"
                    anchorPosition={
                        contextMenu !== null
                            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
                            : undefined
                    }
                >
                    <MenuItem onClick={handleEditDocumentNode} disabled={editNodeDisable}>Edit</MenuItem>
                    <Divider/>
                    <MenuItem onClick={handleInsertBefore} disabled={insertBeforeDisable}>Insert Before</MenuItem>
                    <MenuItem onClick={handleInsertInto} disabled={insertIntoDisable}>Insert Into</MenuItem>
                    <MenuItem onClick={handleInsertAfter} disabled={insertAfterDisable}>Insert After</MenuItem>
                    <Divider/>
                    <MenuItem onClick={handleDeleteFileClick} disabled={!deleteFileEnabled}>Delete</MenuItem>
                    <Divider/>
                    <MenuItem onClick={handleCheckoutClick} disabled={!checkoutEnabled}>Checkout</MenuItem>
                    <MenuItem onClick={handleCheckinClick} disabled={!checkinEnabled}>Checkin</MenuItem>
                    <MenuItem onClick={handleRevokeCheckoutClick} disabled={!checkinEnabled}>Revoke Checkout</MenuItem>
                </Menu>
                <CheckinDialog open={checkinDialogOpen} onCancel={() => setCheckinDialogOpen(false)} node={selectedNode} 
                    onSave={handleCheckinSave} />
            </DialogContent>
            <DialogActions sx={{ padding: 3, paddingTop: 1, display: 'flex', justifyContent: 'space-between' }}>
                <Box sx={{ flexShrink: '3' }}>
                    {/* 
                    <Tooltip title="New File">
                        <IconButton variant="outlined" onClick={() => handleNewFile()} disabled={!newFileEnabled} color="primary">
                            <NoteAddIcon />
                        </IconButton>
                    </Tooltip>
                    {target === 'QRH' &&
                        <Tooltip title="New Folder">
                            <IconButton variant="outlined" onClick={() => handleNewFolder()} disabled={!newFolderEnabled} color="primary">
                                <CreateNewFolderIcon />
                            </IconButton>
                        </Tooltip>
                    }
                    <Tooltip title="Delete">
                        <IconButton disabled={!deleteFileEnabled} onClick={handleDeleteFileClick} color="error" sx={{ marginLeft: '1.5ex' }} >
                            <DeleteIcon fontSize="inherit" />
                        </IconButton>
                    </Tooltip>
                    */}
                </Box>
                <Box>
                    <Button variant="outlined" onClick={() => onOKClick()} sx={{ minWidth: '7em' }} disabled={!okEnabled} >Open</Button>
                    <Button variant="outlined" onClick={onCancelClick} sx={{ minWidth: '7em', marginLeft: 2 }}>Cancel</Button>
                </Box>
                <CreateNewDocumentNode open={newFileDialogOpen} onSave={handleNewFileSave} onCancel={() => setNewFileDialogOpen(false)} target={target} 
                    schemaInfo={schema} ancestors={ancestors} node={selectedNode} rules={schemaRule} position={position} />
                <EditDocumentNode open={editDialogOpen} onSave={handleEditDocumentNodeSave} onCancel={() => setEditDialogOpen(false)} node={selectedNode} />
                <DeleteFileDialog open={deleteFileDialogOpen} onSave={handleDeleteFileDialogSave} onCancel={() => setDeleteFileDialogOpen(false)} target={target} 
                    name={selectedNode?.label}/>
            </DialogActions>
        </Dialog>

    );

}

function ContentInclusionDialog(props) {
    const {
        title = 'Select Content Inclusion',
        open,
        value,
        param,
        onSave,
        onCancel,
        document,
        element,
        ...other
    } = props;
    const [okEnabled, setOkEnabled] = useState(false);
    const [typeValue, setTypeValue] = useState('');
    const [configValue, setConfigValue] = useState('');
    const [internalLinkChooserDialogOpen, setInternalLinkChooserDialogOpen] = useState(false);
    const [tocPreviewValue, setTocPreviewValue] = useState('');
    const [tocLocationValue, setTocLocationtValue] = useState('');
    const [tocLinkendValue, setTocLinkendValue] = useState('');
    const [inclusionList, setInclusionList] = useState([...INCLUSIONS]);

    const TOC_TITLE = 'Choose Table of Contents Context Element';

    

    useEffect(() => {
        if (open) {
            setOkEnabled(false);
            setConfigValue({});
            if (element?.attributes?.config) {
                parseConfig(element.attributes.config);
            }
            if (value) {
                setTypeValue(value);
            } else {
                setTypeValue('');
            }
        } else {
            setTypeValue('');
        }
    }, [open, document]);

    useEffect(() => {
        if (element) {
            setConfigValue('{}')
            if (element.attributes?.config) {
                setConfigValue(element.attributes.config);
                parseConfig(element.attributes?.config);
            }
        }
    }, [element]);

    useEffect(() => {
        if ( param ) {
            console.log('CI params: ' + JSON.stringify(param));
            parseParams(param);
        } else {
            setInclusionList([...INCLUSIONS]);
        }
    },[param]);

    const parseConfig = (config) => {
        if (config) {
            setConfigValue(config);
            try {
                const c = JSON.parse(config);
                if (c.linkend) {
                    setTocLinkendValue(c.linkend);
                } else {
                    setTocLinkendValue('');
                }
                if (c.preview) {
                    setTocPreviewValue(c.preview);
                } else {
                    setTocPreviewValue('');
                }
                if (c.location) {
                    setTocLocationtValue(c.location);
                } else {
                    setTocLocationtValue('');
                }
            } catch (error) {
                // ignore
            }
        }
    }

    const parseParams = (params) => {
        if (params) {
            if (Array.isArray(params.values)) {
                setInclusionList(params.values);
            } else {
                try {
                    const c = JSON.parse(params);
                    if (Array.isArray(c.values)) {
                        setInclusionList(c.values);
                    }
                } catch (error) {
                    console.log('Error parsing CI params: ' + error);
                }
            }
        }
    };

    const onTocClick = () => {
        setInternalLinkChooserDialogOpen(true);
    }

    const handleLinkChooserSave = (value) => {
        setInternalLinkChooserDialogOpen(false);
        console.log('Context: value= ' + JSON.stringify(value));
        const cv = { preview: '', location: '', preview: '' };
        if (value.preview) {
            setTocPreviewValue(value.preview);
            cv.preview = value.preview;
        } else {
            setTocPreviewValue('');
        }
        if (value.linkend) {
            setTocLinkendValue(value.linkend);
            cv.linkend = value.linkend;
        } else {
            setTocLinkendValue('');
        }
        if (value.location) {
            setTocLocationtValue(value.location);
            cv.location = value.location;
        } else {
            setTocLocationtValue('');
        }
        setConfigValue(cv);
        setOkEnabled(true);
    };

    const onOKClick = () => {
        if (typeof onSave === 'function') {
            onSave(typeValue, configValue);
        }
    }


    const onCancelClick = () => {
        onClose();
    };

    const onClose = (event, reason) => {
        if (reason === 'backdropClick') {
            return;
        }
        if (typeof onCancel === 'function') {
            onCancel();
        }
    };

    const handleTypeChange = (event) => {
        setOkEnabled(true);
        setTypeValue(event.target.value);
    };

    return (
        <Dialog
            maxWidth={'900px'}
            open={open}
            onClose={onClose}
            slotProps={{
                paper: {
                    sx: {
                        minWidth: 700,
                        maxWidth: 900,
                        maxHeight: 550,
                        minHeight: 500,
                    }
                }
            }}
        >
            <DialogTitle sx={{ fontWeight: 'bold' }}>{title}</DialogTitle>
            <DialogContent>
                <FormControl fullWidth sx={{ marginTop: 3 }}>
                    <InputLabel id="incls-select-label">Inclusion Type</InputLabel>
                    <Select
                        labelId="incls-select-label"
                        id="incls-select"
                        value={typeValue}
                        label="Inclusion Type"
                        onChange={handleTypeChange}
                    >
                        {inclusionList.map(item =>
                            <MenuItem value={item.value}>{item.label}</MenuItem>
                        )}
                    </Select>
                </FormControl>
                {typeValue === 'TOC' &&
                    <React.Fragment>
                        <Box sx={{ paddingTop: 2 }}>
                            <FormControl sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginTop: 6 }}>
                                <FormLabel id="preview-label" sx={{ fontWeight: 'bold', width: '6.5em', color: 'black', marginRight: 1 }}>Context:</FormLabel>

                                <TextField fullWidth value={tocPreviewValue} size="small" variant="outlined" aria-labelledby="preview-label"
                                    aria-readonly={true}
                                    slotProps={{
                                        input: {
                                            readOnly: true,
                                            sx: {
                                                color: 'rgba(0,0,0,0.5)'
                                            }
                                        }
                                    }} />
                                <Button variant="outlined" onClick={() => onTocClick()} sx={{ marginLeft: 1 }}>Choose...</Button>
                            </FormControl>
                        </Box>
                        <InternalLinkChooserDialog title={TOC_TITLE} document={document} config={configValue}
                            open={internalLinkChooserDialogOpen} onCancel={() => setInternalLinkChooserDialogOpen(false)}
                            onSave={handleLinkChooserSave} />
                    </React.Fragment>
                }

            </DialogContent>
            <DialogActions sx={{ padding: 3, paddingTop: 1 }}>
                <Box>
                    <Button variant="outlined" onClick={() => onOKClick()} sx={{ minWidth: '7em' }} disabled={!okEnabled} >OK</Button>
                    <Button variant="outlined" onClick={onCancelClick} sx={{ minWidth: '7em', marginLeft: 2 }}>Cancel</Button>
                </Box>
            </DialogActions>
        </Dialog>

    );

}


function ChooseFootnoteDialog(props) {
    const {
        title = 'Select Footnote',
        open,
        value,
        onSave,
        onCancel,
        document,
        element,
        ...other
    } = props;
    const [okEnabled, setOkEnabled] = useState(false);
    const [footnotes, setFootnotes] = useState([]);
    const [idValue, setIdValue] = useState('');
    const [linkend, setLinkend] = useState('');

    useEffect(() => {
        if (open) {
            setOkEnabled(false);


        } else {

        }
    }, [open, document]);

    useEffect(() => {
        // console.log('FootnoteRef Info: ' + JSON.stringify(element));
        if (element) {
            if (Array.isArray(element.targets)) {
                setFootnotes(element.targets);
            } else {
                setFootnotes([]);
            }
            if (element.linkend) {
                setLinkend(element.linkend);
            } else {
                setLinkend('');
            }
            if (element.id) {
                setIdValue(element.id);
            } else {
                setIdValue('');
            }


        } else {
            setFootnotes([]);
            setLinkend('');
            setIdValue('');
        }
    }, [element]);

    const handleRowClick = (event, rid) => {
        setLinkend(rid);
        setOkEnabled(true);
    };

    const onOKClick = () => {
        if (typeof onSave === 'function') {
            // onSave(typeValue, configValue);
            onSave({ linkend: linkend })
        }
    }


    const onCancelClick = () => {
        onClose();
    };

    const onClose = (event, reason) => {
        if (reason === 'backdropClick') {
            return;
        }
        if (typeof onCancel === 'function') {
            onCancel();
        }
    };



    return (
        <Dialog
            maxWidth={'900px'}
            open={open}
            onClose={onClose}
            slotProps={{
                paper: {
                    sx: {
                        minWidth: 700,
                        maxWidth: 900,
                        maxHeight: 550,
                        minHeight: 500,
                    }
                }
            }}
        >
            <DialogTitle sx={{ fontWeight: 'bold' }}>{title}</DialogTitle>
            <DialogContent>
                <TableContainer sx={{ maxHeight: 440 }}>
                    <Table stickyHeader aria-label="footnote table" sx={{ width: "100%" }}>
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    key="col1"
                                    align="left"
                                    style={{ minWidth: "10%" }}
                                >
                                    ID
                                </TableCell>
                                <TableCell
                                    key="col2"
                                    align="left"

                                >
                                    Content
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {Array.isArray(footnotes) &&
                                footnotes.map(footnote =>
                                    <TableRow key={footnote.id} onClick={(event) => handleRowClick(event, footnote.id)} selected={footnote.id === linkend}>
                                        <TableCell key={`I${footnote.id}`} sx={{ width: '18em', whiteSpace: 'nowrap' }}>{footnote.id}</TableCell>
                                        <TableCell key={`C${footnote.id}`}>{footnote.content}</TableCell>
                                    </TableRow>
                                )
                            }
                            {/* rows

                                .map((row) => {
                                    return (
                                        <TableRow hover role="checkbox" tabIndex={-1} key={row.code}>
                                            {columns.map((column) => {
                                                const value = row[column.id];
                                                return (
                                                    <TableCell key={column.id} align={column.align}>
                                                        {column.format && typeof value === 'number'
                                                            ? column.format(value)
                                                            : value}
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    );
                                }) */}
                        </TableBody>
                    </Table>
                </TableContainer>


            </DialogContent>
            <DialogActions sx={{ padding: 3, paddingTop: 1 }}>
                <Box>
                    <Button variant="outlined" onClick={() => onOKClick()} sx={{ minWidth: '7em' }} disabled={!okEnabled} >OK</Button>
                    <Button variant="outlined" onClick={onCancelClick} sx={{ minWidth: '7em', marginLeft: 2 }}>Cancel</Button>
                </Box>
            </DialogActions>
        </Dialog>

    );

}

export { ChooseDocumentUnitDialog, ContentInclusionDialog, ChooseFootnoteDialog };